import React from 'react';

import useDutchiePayEnrollment from 'hooks/use-dutchiePay-enrollment';
import { Button } from 'components/core';
import { useAccountNavBar } from 'src/components/embedded-account/account-nav-bar/use-account-nav-bar';
import { RECENT_ACTIVITY_SECTION_ID } from 'src/components/wallet/recent-activity-section';
import { useAddFundsModal } from 'src/components/wallet/add-funds-modal';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useLoyalty } from 'src/hooks/use-loyalty';
import { usePrepaidWalletBalance } from 'src/hooks/use-prepaid-wallet-balance';
import { editBankAccount } from 'src/payments/constants';
import { useDutchiePay } from 'src/payments/hooks';
import { useOpenDutchiePay } from 'src/payments/hooks/use-open-dutchie-pay/use-open-dutchie-pay';
import { handleScroll } from 'src/utils/scroll';

import FullStar from 'src/assets/full-star';
import {
  BalanceAmount,
  BalanceLabel,
  BrandGroup,
  BrandLogo,
  CardContainer,
  CardHeaderRow,
  InnerCard,
  PointsBadge,
  PointsBadgeChevron,
  RewardsLink,
  RewardsRow,
  RewardsRowIcon,
  RewardsText,
  ViewActivityButton,
  WalletLabel,
  ButtonRow,
} from './wallet-balance-card.styles';

export const WalletBalanceCard = (): JSX.Element => {
  const { dispensary } = useDispensary();
  const { accountRoutes, shouldDisplayLoyaltySection } = useAccountNavBar();
  const { activeBankAccount } = useDutchiePay();
  const { open: openAddFundsModal } = useAddFundsModal();
  const { formattedBalance } = usePrepaidWalletBalance();
  const { loyaltyPoints } = useLoyalty();
  const DutchiePayEnrollment = useDutchiePayEnrollment();
  const { openDutchiePay } = useOpenDutchiePay({ step: editBankAccount });

  const loyaltyRoute = shouldDisplayLoyaltySection ? accountRoutes?.loyalty : undefined;
  const hasLoyaltyPoints = Number.isInteger(Number.parseInt(loyaltyPoints, 10));

  const handleLinkBankClick = (): void => {
    openDutchiePay(editBankAccount);
    DutchiePayEnrollment.editBankAccount();
  };

  const handleAddFundsClick = (): void => {
    void openAddFundsModal(undefined);
  };

  return (
    <CardContainer aria-labelledby='wallet-balance-card-label'>
      <InnerCard>
        <CardHeaderRow>
          <BrandGroup>
            {dispensary?.logoImage ? <BrandLogo src={dispensary.logoImage} alt='' /> : null}
            <WalletLabel id='wallet-balance-card-label' size='xxsmall' weight='bold' casing='uppercase' tag='span'>
              Wallet
            </WalletLabel>
          </BrandGroup>
          {loyaltyRoute && hasLoyaltyPoints ? (
            <PointsBadge href={loyaltyRoute}>
              {loyaltyPoints} pts
              <PointsBadgeChevron aria-hidden />
            </PointsBadge>
          ) : null}
        </CardHeaderRow>
        <BalanceLabel size='xxsmall' weight='semi' casing='uppercase' tag='span'>
          Balance
        </BalanceLabel>
        <BalanceAmount size='xlarge' tag='p'>
          {formattedBalance}
        </BalanceAmount>
        {!activeBankAccount ? (
          <Button type='button' variant='primary' size='medium' fluid onClick={handleLinkBankClick}>
            + Link bank to add funds
          </Button>
        ) : (
          <ButtonRow>
            <Button type='button' variant='primary' size='medium' fluid onClick={handleAddFundsClick}>
              + Add funds
            </Button>
            <ViewActivityButton
              href={`#${RECENT_ACTIVITY_SECTION_ID}`}
              variant='outlinePrimary'
              size='medium'
              fluid
              onClick={handleScroll}
            >
              View activity
            </ViewActivityButton>
          </ButtonRow>
        )}
      </InnerCard>
      {loyaltyRoute ? (
        <RewardsRow href={loyaltyRoute} aria-label='Earn 2x rewards on all wallet purchases. See your rewards.'>
          <RewardsRowIcon aria-hidden>
            <FullStar width={14} height={14} fill='currentColor' />
          </RewardsRowIcon>
          <RewardsText size='medium' tag='span'>
            Earn 2x rewards on all wallet purchases
          </RewardsText>
          <RewardsLink aria-hidden>See your rewards →</RewardsLink>
        </RewardsRow>
      ) : null}
    </CardContainer>
  );
};
